import * as m from '@staiter/i18n';
import { TriangleAlert } from 'lucide-react';
import type { JSX } from 'react';
import { formatDate } from '~/lib/format';
import { shiftIsoDate } from '~/lib/tasks/api/blueprints';
import { businessTodayIso } from '~/lib/tasks/business-day';
import styles from './dialogs.module.css';

/**
 * F4.3 — единый компонент превью якоря (день 0 + расчётный диапазон), общий для
 * трёх поверхностей: materialize-диалог, duplicate (режим shift), /projects/new
 * prefill. Слепой ввод даты запрещён дизайном — превью показывает результат до
 * коммита (MAJOR-находка /ux-designer: неравная date-safety на путях).
 *
 * Работает от примитивов (hasDates/maxOffsetDays/anchorTitle), а не от снапшота —
 * поэтому одинаково применим там, где снапшот есть (materialize/prefill) и где
 * его нет клиентски (duplicate — span берётся из дат проекта-источника).
 */
export type AnchorPreviewProps = {
  hasDates: boolean;
  maxOffsetDays: number;
  /** Заголовок задачи на дне 0 (если известен). */
  anchorTitle?: string | null;
  /**
   * FE-11 — режим date-якоря (дубликат в shift): у дубля нет единой anchor-задачи,
   * якорь — ДАТА начала проекта. Показываем «День 0 — дата начала проекта {день0}»,
   * а не фальшивую задачу (решение /ux-designer B: честный предпросмотр важнее
   * визуального единообразия строки, но паттерн строки «День 0 —» сохраняем).
   */
  dateAnchor?: boolean;
  /** Введённая пользователем дата. */
  date: string | null;
  dateMode: 'start_on' | 'finish_by';
  /** true → снапшот «одни дедлайны» (лейбл «дата первого дедлайна»). */
  dueOnly?: boolean;
};

export function AnchorPreview({
  hasDates,
  maxOffsetDays,
  anchorTitle,
  dateAnchor = false,
  date,
  dateMode,
}: AnchorPreviewProps): JSX.Element {
  const day0 =
    date && hasDates
      ? dateMode === 'finish_by'
        ? shiftIsoDate(date, -maxOffsetDays)
        : date
      : null;
  const firstDate = day0;
  const lastDate = day0 ? shiftIsoDate(day0, maxOffsetDays) : null;

  /*
   * F15 — «план в прошлом». В режиме «завершить к» день 0 вычисляется НАЗАД от
   * введённой даты, и при близком дедлайне уезжает за сегодня; пользователь
   * узнавал об этом только по созданному проекту с просроченными задачами.
   * Сравнение строк ISO корректно (обе — YYYY-MM-DD), «сегодня» берётся
   * локальное — иначе до 05:00 по KZ предупреждение врало бы в обе стороны.
   * Не блокирует: план в прошлом бывает и намеренным (доразбор старого заказа).
   */
  const startsInPast = firstDate !== null && firstDate < businessTodayIso();

  return (
    <div className={styles.anchorPreview}>
      {hasDates ? (
        <>
          {dateAnchor ? (
            <div className={styles.anchorLine}>
              {firstDate
                ? m.tmpl_anchor_day0_date({ date: formatDate(firstDate) })
                : m.tmpl_anchor_day0_date_pending()}
            </div>
          ) : (
            anchorTitle && (
              <div className={styles.anchorLine}>{m.tmpl_anchor_day0({ title: anchorTitle })}</div>
            )
          )}
          {firstDate && lastDate ? (
            <div className={styles.anchorLine}>
              {m.tmpl_anchor_range({ first: formatDate(firstDate), last: formatDate(lastDate) })}
            </div>
          ) : (
            // F100 круг 7 (UX): без даты расчёт не пропадает молча — блок говорит, чего ждёт.
            <div className={styles.anchorPending}>{m.tmpl_anchor_range_pending()}</div>
          )}
          {startsInPast && firstDate && (
            <div className={styles.anchorWarn}>
              <TriangleAlert size={13} aria-hidden="true" />
              <span>
                {m.tmpl_anchor_past_warn({ date: formatDate(firstDate) })}
                {/* F-D7 — рядом с фактом стоит выход. Предупреждение, которое
                    называет проблему и молчит про способ её убрать, оставляет
                    пользователя ровно там же, где застал. Способ разный:
                    в «завершить к» день 0 считается назад, и двигать надо режим
                    или дату завершения; в «от старта» достаточно выбрать день
                    позже. */}{' '}
                <span className={styles.anchorHint}>
                  {dateMode === 'finish_by'
                    ? m.tmpl_anchor_past_hint_finish()
                    : m.tmpl_anchor_past_hint_start()}
                </span>
              </span>
            </div>
          )}
        </>
      ) : (
        /* F-D6 — у дубликата источник ДАННЫХ другой: не шаблон, а проект.
           Неверная сущность в превью подрывает доверие ровно там, где человек
           решает «как есть или сдвинуть». */
        <div className={styles.anchorLine}>
          {dateAnchor ? m.tmpl_anchor_no_dates_project() : m.tmpl_anchor_no_dates()}
        </div>
      )}
    </div>
  );
}
